import type { Router } from 'vue-router';

export interface RoutableNotification {
  type: string;
  payload?: {
    accountId?: string | number;
    programId?: string | number;
    queueItemId?: number;
    submissionId?: string | number;
  };
}

export interface NotificationNavigationOptions {
  forceRouteRefresh?: boolean;
}

export async function routeFromNotification(
  router: Router,
  notification: RoutableNotification,
  options: NotificationNavigationOptions = {},
): Promise<void> {
  const accountId = notification.payload?.accountId;
  const programId = notification.payload?.programId;
  const submissionId = notification.payload?.submissionId;
  const forceRouteRefresh = options.forceRouteRefresh ?? false;

  const navigate = async (path: string): Promise<void> => {
    if (forceRouteRefresh) {
      // if user is already at the target route and click a notification to that route, vue-router
      // suppresses duplicate navigation; bouncing through '/' guarantees route actually changes
      await router.push('/');
      await router.replace(path);
    } else {
      await router.push(path);
    }
  };

  switch (notification.type) {
    case 'new-policies-added':
      if (!accountId) return;
      await navigate(`/account/${accountId}/policy-detail`);
      return;
    case 'policies-up-for-renewal':
      if (!accountId || !programId) return;
      await navigate(`/account/${accountId}/programs/${programId}`);
      return;
    case 'new-document-upload':
    case 'binder-ready-for-validation':
      if (!accountId) return;
      await navigate(`/account/${accountId}/documents`);
      return;
    case 'external-policy-ready-for-review':
      if (!accountId || !submissionId) return;
      await navigate(`/account/${accountId}/policy-detail/external-policies/review/${submissionId}`);
      return;
    case 'document-exception':
      await navigate('/ai-results');
      return;
    default:
      return;
  }
}
